<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="text-h3 mb-6">{{ $t('global.adminBilling.title') }}</h1>

        <!-- Tab navigation -->
        <v-tabs v-model="activeTab" class="mb-6">
          <v-tab>{{ $t('global.adminBilling.devices') }}</v-tab>
          <v-tab>{{ $t('global.adminBilling.transactions') }}</v-tab>
          <v-tab>{{ $t('global.adminBilling.notificationLog') }}</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- Devices Tab -->
          <v-tab-item>
            <div class="d-flex justify-end mb-2">
              <v-btn text color="primary" @click="$refs.deviceTable.refresh()">
                <v-icon left>mdi-refresh</v-icon>
                Obnovit
              </v-btn>
            </div>
            <!-- Device Subscription Status Section -->
            <billing-device-table
              ref="deviceTable"
              @set-status="handleSetStatus"
              @set-activation-state="handleSetActivationState"
              @gift-subscription="handleGiftSubscription"
              @send-billing-reminder="handleSendBillingReminder"
              @show-debug="handleShowDebug"
            />
          </v-tab-item>

          <!-- Transactions Tab -->
          <v-tab-item>
            <v-card>
              <v-card-title>
                <span class="text-h5">{{ $t('global.adminBilling.recentTransactions') }}</span>
                <v-spacer />
                <v-btn text color="primary" @click="$refs.transactionTable.refresh()">
                  <v-icon left>mdi-refresh</v-icon>
                  Obnovit
                </v-btn>
              </v-card-title>
              <v-card-text>
                <billing-transaction-table ref="transactionTable" @show-debug="handleShowDebug" />
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Notification Log Tab -->
          <v-tab-item>
            <div class="mb-4 d-flex justify-space-between align-center">
              <h2 class="text-h5">{{ $t('global.adminBilling.notificationHistory') }}</h2>
              <send-notification-modal @success="handleNotificationSent" />
            </div>
            <subscription-notification-log-table ref="notificationLogTable" />
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>

    <!-- Modals -->
    <set-subscription-status-modal
      v-model="statusModal.show"
      :device="statusModal.device"
      @success="handleStatusUpdated"
    />

    <set-billing-activation-state-modal
      v-model="activationStateModal.show"
      :device="activationStateModal.device"
      @success="handleActivationStateUpdated"
    />

    <gift-subscription-modal
      v-model="giftModal.show"
      :device="giftModal.device"
      @success="handleGiftCreated"
    />

    <billing-debug-modal v-model="debugModal.show" :device="debugModal.device" />

    <send-billing-reminder-modal
      v-model="billingReminderModal.show"
      :device="billingReminderModal.device"
      @success="handleBillingReminderSent"
    />
  </v-container>
</template>

<script>
import BillingDeviceTable from '@/components/Admin/Billing/BillingDeviceTable.vue'
import BillingTransactionTable from '@/components/Admin/Billing/BillingTransactionTable.vue'
import SetSubscriptionStatusModal from '@/components/Admin/Billing/SetSubscriptionStatusModal.vue'
import SetBillingActivationStateModal from '@/components/Admin/Billing/SetBillingActivationStateModal.vue'
import GiftSubscriptionModal from '@/components/Admin/Billing/GiftSubscriptionModal.vue'
import BillingDebugModal from '@/components/Admin/Billing/BillingDebugModal.vue'
import SendBillingReminderModal from '@/components/Admin/Billing/SendBillingReminderModal.vue'
import SubscriptionNotificationLogTable from '@/components/Admin/Billing/SubscriptionNotificationLogTable.vue'
import SendNotificationModal from '@/components/Admin/Billing/SendNotificationModal.vue'

export default {
  name: 'AdminBilling',

  components: {
    BillingDeviceTable,
    BillingTransactionTable,
    SetSubscriptionStatusModal,
    SetBillingActivationStateModal,
    GiftSubscriptionModal,
    BillingDebugModal,
    SendBillingReminderModal,
    SubscriptionNotificationLogTable,
    SendNotificationModal,
  },

  data() {
    return {
      activeTab: 0,
      statusModal: {
        show: false,
        device: null,
      },
      activationStateModal: {
        show: false,
        device: null,
      },
      giftModal: {
        show: false,
        device: null,
      },
      debugModal: {
        show: false,
        device: null,
      },
      billingReminderModal: {
        show: false,
        device: null,
      },
    }
  },

  methods: {
    handleSetStatus(device) {
      this.statusModal.device = device
      this.statusModal.show = true
    },

    handleSetActivationState(device) {
      this.activationStateModal.device = device
      this.activationStateModal.show = true
    },

    handleGiftSubscription(device) {
      this.giftModal.device = device
      this.giftModal.show = true
    },

    handleShowDebug(device) {
      this.debugModal.device = device
      this.debugModal.show = true
    },

    handleSendBillingReminder(device) {
      this.billingReminderModal.device = device
      this.billingReminderModal.show = true
    },

    handleStatusUpdated() {
      this.statusModal.show = false
      this.statusModal.device = null
      // Refresh both tables to show updated data
      this.$refs.deviceTable.refresh()
      this.$refs.transactionTable.refresh()
    },

    handleActivationStateUpdated() {
      this.activationStateModal.show = false
      this.activationStateModal.device = null
      // Refresh tables to show updated data (transaction table lives in an inactive tab)
      this.$refs.deviceTable?.refresh()
      this.$refs.transactionTable?.refresh()
    },

    handleGiftCreated() {
      console.log('AdminBilling: handleGiftCreated called')
      console.log('AdminBilling: giftModal.show before:', this.giftModal.show)
      this.giftModal.show = false
      console.log('AdminBilling: giftModal.show after:', this.giftModal.show)

      // Delay clearing device until modal animation completes
      setTimeout(() => {
        this.giftModal.device = null
        console.log('AdminBilling: Device cleared after delay')
      }, 300)

      // Use nextTick to ensure state updates are processed
      this.$nextTick(() => {
        console.log('AdminBilling: giftModal.show in nextTick:', this.giftModal.show)
        // Refresh both tables to show updated data
        this.$refs.deviceTable.refresh()
        this.$refs.transactionTable.refresh()
        console.log('AdminBilling: Modal closed and tables refreshed')
      })
    },

    handleBillingReminderSent() {
      this.billingReminderModal.show = false
      this.billingReminderModal.device = null
      this.$toast.success('Billing reminder email byl úspěšně odeslán')
    },

    handleNotificationSent() {
      // Refresh the notification log table
      if (this.$refs.notificationLogTable) {
        this.$refs.notificationLogTable.refresh()
      }
      this.$toast.success(this.$t('global.adminBilling.notificationSentSuccess'))
    },
  },
}
</script>

<style scoped></style>
